import { useEffect, useRef, type MouseEvent, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/** 지금 열려 있는 다이얼로그(Modal·Drawer) — 마지막이 맨 위. Esc·Tab 은 맨 위 하나만 처리한다 */
const stack: object[] = [];

/**
 * 다이얼로그 공통 동작 — Modal·Drawer 가 함께 쓴다 (D-09 §15.6)
 * - 포커스 트랩 + Esc 닫기. 겹쳐 열려 있으면(팝업+팝업, 팝업+Drawer, Drawer+팝업) 맨 위 하나만 반응한다.
 * - 열기 시 첫 요소로 포커스(본문 autoFocus 가 이미 가져갔으면 그대로) / 닫기 시 열기 직전 포커스로 복원.
 *   복원 대상은 열리는 렌더 시점에 잡는다 — effect 시점엔 본문 autoFocus 가 이미 포커스를 가져가 있을 수 있다.
 * - onClose 는 ref 로 최신값만 읽는다. 호출부가 인라인 함수를 넘겨도 effect 가 다시 돌지 않아
 *   입력 중 포커스가 첫 버튼(✕)으로 튀지 않는다.
 * - 반환값 scrimProps: 누른 곳과 뗀 곳이 모두 스크림일 때만 닫는다. 안↔밖으로 끌다 떼면 브라우저가 click 을
 *   공통 조상인 스크림에 주므로 따로 걸러 낸다. mousedown 은 capture 로 받아 본체가 전파를 막아도 매번 갱신된다.
 */
export function useDialog(open: boolean, onClose: () => void, boxRef: RefObject<HTMLElement>) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const openerRef = useRef<HTMLElement | null>(null);
  if (!open) openerRef.current = null;
  else if (!openerRef.current && typeof document !== 'undefined') {
    openerRef.current = document.activeElement as HTMLElement | null;
  }

  useEffect(() => {
    if (!open) return;
    const me = {};
    stack.push(me);
    const opener = openerRef.current;
    const box = boxRef.current;
    const getItems = () => (box ? Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE)) : []);

    if (!box?.contains(document.activeElement)) (getItems()[0] ?? box)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== me) return;
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const items = getItems();
      if (items.length === 0) { e.preventDefault(); box?.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      stack.splice(stack.indexOf(me), 1);
      if (opener?.isConnected) opener.focus();
    };
  }, [open, boxRef]);

  const downOnScrim = useRef(false);
  const scrimProps = {
    onMouseDownCapture: (e: MouseEvent) => { downOnScrim.current = e.target === e.currentTarget; },
    onMouseUp: (e: MouseEvent) => { if (e.target !== e.currentTarget) downOnScrim.current = false; },
    onClick: (e: MouseEvent) => {
      if (downOnScrim.current && e.target === e.currentTarget) onCloseRef.current();
      downOnScrim.current = false;
    },
  };
  return { scrimProps };
}
